import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SnapshotTypeEnum } from '../../model/snapshot_type';
import Utils from '../../utils/immersive_utils';
import usePersistentState from '../use-persistent-state';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { MarketChangeContext } from './market-change-provider';

// IM-2598 - Market Change surface data layer (constant-maturity OR expiry).
//
// Self-contained historic fetch: reads the shared selection (market, T1, T2)
// from MarketChangeContext and issues TWO historic_vol_surface requests, one at
// each timestamp, model 'SVI', at the current snapshotType (CM constant-maturity
// tenors or EE listed expiries - the request `type` param). Each selection is a new
// GENERATION: request ids carry the generation (`${KEY}_${slot}_${gen}`) and the
// response handler drops any reply whose generation is not the current one, so an
// out-of-order WS response for a superseded market/T1/T2 can never write a stale
// surface into a slot (distinct from a provider that keys writes off a fixed
// per-slot id, which is materially more exposed to that race). The prior
// generation's channels are unsubscribed before the new ones open, and on
// unmount. WebSocketContext is app-wide (mounted above the dashboard tab switch),
// so nothing else needs mounting; a pure historic fetch needs no live GetVol
// provider.
//
// Vols arrive as FRACTIONS (e.g. 0.65). We keep them as fractions here; the view
// multiplies by 100 for display.

// tenor -> field -> value. Fields are the PUT/CALL columns the surface tables
// render: 'Future' (a price) plus the vol pillars in SURFACE_VOL_FIELDS (vol
// fractions). Only fields actually present are kept, so an absent cell is absent
// (never zero).
export type SurfaceGrid = Record<string, Record<string, number>>;

// Vol fields (fractions) retained from the payload and differenced. 'ATMF' reads
// the payload's DN pillar (or ATMF when DN is absent); the rest read directly.
// This is the full set across BOTH display modes (PUT/CALL and RR/FLY) - the panel
// picks which subset to show as columns. 'Future' is kept too but outside this
// list: it is a price, formatted differently (and IS differenced, shown as the
// futures move).
export const SURFACE_VOL_FIELDS = [
  '10P',
  '25P',
  'ATMF',
  '25C',
  '10C',
  '25FLY',
  '25RR',
  '10FLY',
  '10RR',
] as const;

export interface MarketChangeSurface {
  timestamp: string;
  tenors: SurfaceGrid;
}

interface IMarketChangeSurfaceProvider {
  // null = no data (not yet fetched, or the fetch returned nothing usable).
  surfaceT1: MarketChangeSurface | null;
  surfaceT2: MarketChangeSurface | null;
  // S(T2) - S(T1), cell-by-cell on the grid common to both. A cell missing on
  // either side is absent from the delta (NOT zero).
  surfaceDelta: SurfaceGrid | null;
  // CM (constant-maturity tenors) vs EE (listed expiries). Persisted; changing it
  // re-fetches both slots at the new type.
  snapshotType: SnapshotTypeEnum;
  setSnapshotType: (type: SnapshotTypeEnum) => void;
  loading: boolean;
  error: string | null;
}

interface IMarketChangeSurfaceParamsProvider {
  children?: ReactNode;
}

type SurfaceSlot = 'T1' | 'T2';

const KEY = 'MARKET_CHANGE_SURFACE';
const STATE_PATH = ['MARKET_CHANGE_SURFACE'];

export const MarketChangeSurfaceContext = createContext({} as IMarketChangeSurfaceProvider);

// Parse a historic_vol_surface result into the canonical row x field grid the
// surface tables render: 'Future' (a price) plus the SURFACE_VOL_FIELDS vols. Rows
// are keyed by whatever the RESPONSE returns - every constant-maturity tenor (CM)
// or every listed expiry date (EE) - so no tenor the surface carries is dropped
// (the prior fixed-subset iteration silently lost any tenor outside it). The ATM
// pillar is 'DN' in the payload but shows as 'ATMF', so the ATMF column reads DN
// (or ATMF when DN is absent). Anything non-finite is dropped (absent, not zero).
// Row ORDER is not decided here - selectSurfaceRows orders the shared row set.
export function parseSurface(result: any): MarketChangeSurface | null {
  if (!result || !result.timestamp || !result.tenors) return null;

  const tenors: SurfaceGrid = {};

  for (const key of Object.keys(result.tenors)) {
    const row = result.tenors[key];
    if (!row) continue;

    const fields: Record<string, number> = {};

    if (typeof row.Future === 'number' && Number.isFinite(row.Future)) {
      fields.Future = row.Future;
    }

    for (const field of SURFACE_VOL_FIELDS) {
      const raw = field === 'ATMF' ? (row.DN ?? row.ATMF) : row[field];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        fields[field] = raw;
      }
    }

    if (Object.keys(fields).length > 0) {
      tenors[key] = fields;
    }
  }

  if (Object.keys(tenors).length === 0) return null;
  return { timestamp: result.timestamp, tenors };
}

// S(T2) - S(T1) over the intersection of the two grids, walking T2's rows (a row
// present only at T1 is an expired/absent row and carries no change). The vol
// columns AND the Future price are differenced (the Change table shows the
// futures move too). A cell yields a delta only where present and finite on BOTH
// sides; everything else is omitted (never zeroed).
export function computeSurfaceDelta(
  t1: MarketChangeSurface | null,
  t2: MarketChangeSurface | null,
): SurfaceGrid | null {
  if (!t1 || !t2) return null;

  const delta: SurfaceGrid = {};

  for (const key of Object.keys(t2.tenors)) {
    const row1 = t1.tenors[key];
    const row2 = t2.tenors[key];
    if (!row1 || !row2) continue;

    const fields: Record<string, number> = {};
    if (typeof row1.Future === 'number' && typeof row2.Future === 'number') {
      fields.Future = row2.Future - row1.Future;
    }
    for (const field of SURFACE_VOL_FIELDS) {
      const v1 = row1[field];
      const v2 = row2[field];
      if (typeof v1 === 'number' && typeof v2 === 'number') {
        fields[field] = v2 - v1;
      }
    }

    if (Object.keys(fields).length > 0) {
      delta[key] = fields;
    }
  }

  if (Object.keys(delta).length === 0) return null;
  return delta;
}

// Ordered row universe shared by the three surface tiles. PURE so it is unit-
// testable. CM: constant-maturity tenors align across T1/T2, so the universe is
// every tenor present on either side, ordered by Utils.sortTenors, none faded. EE:
// the universe is driven by T2's listed expiries (chronological, ISO date keys so
// a lexical sort is chronological); an expiry new in T2 (absent at T1) is FADED -
// its dS is blank, there being no T1 to diff - while an expiry that existed at T1
// but has expired by T2 is simply not in the T2 universe and so is omitted.
export interface SurfaceRow {
  key: string;
  faded: boolean;
}

export function selectSurfaceRows(
  t1: MarketChangeSurface | null,
  t2: MarketChangeSurface | null,
  snapshotType: SnapshotTypeEnum,
): SurfaceRow[] {
  if (snapshotType === SnapshotTypeEnum.CM) {
    const keys = new Set<string>();
    if (t1) Object.keys(t1.tenors).forEach((k) => keys.add(k));
    if (t2) Object.keys(t2.tenors).forEach((k) => keys.add(k));
    const ordered = (Utils.sortTenors(Array.from(keys)) as string[]) ?? [];
    return ordered.map((key) => ({ key, faded: false }));
  }

  if (!t2) return [];
  const t1Keys = t1 ? new Set(Object.keys(t1.tenors)) : new Set<string>();
  return Object.keys(t2.tenors)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({ key, faded: !t1Keys.has(key) }));
}

export const MarketChangeSurfaceProvider = (props: IMarketChangeSurfaceParamsProvider) => {
  const { children } = props;

  const { sendMessage, subscribeToChannel, unsubscribeFromChannel } = useContext(WebSocketContext);
  const { market, t1, t2 } = useContext(MarketChangeContext);

  const [surfaceT1, setSurfaceT1] = useState<MarketChangeSurface | null>(null);
  const [surfaceT2, setSurfaceT2] = useState<MarketChangeSurface | null>(null);
  const [snapshotType, setSnapshotType] = usePersistentState<SnapshotTypeEnum>(
    STATE_PATH,
    'snapshotType',
    SnapshotTypeEnum.CM,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each market/T1/T2 selection bumps genRef; request ids embed the generation
  // and the handler ignores any response from a stale one.
  const genRef = useRef(0);
  const inFlightRef = useRef<Set<string>>(new Set());
  // Channels subscribed for the current generation, held so they can be torn
  // down before the next generation opens and on unmount.
  const subscribedRef = useRef<Set<string>>(new Set());
  const pendingRef = useRef<Set<SurfaceSlot>>(new Set());
  const handleRef = useRef<(slot: SurfaceSlot, gen: number, res: any) => void>(() => {});

  const markResolved = useCallback((slot: SurfaceSlot) => {
    pendingRef.current.delete(slot);
    if (pendingRef.current.size === 0) setLoading(false);
  }, []);

  // State setters are stable, so this handler never needs to be rebuilt; the
  // subscription callback reads handleRef.current to stay fresh. The generation
  // is captured per request, so a late response from a superseded selection is
  // dropped here before any slot is written.
  const handleResponse = useCallback(
    (slot: SurfaceSlot, gen: number, res: any) => {
      if (gen !== genRef.current) return;

      inFlightRef.current.delete(`${KEY}_${slot}_${gen}`);

      if (!res || res.error || !res.result) {
        setError('Failed to load vol surface');
        markResolved(slot);
        return;
      }

      const parsed = parseSurface(res.result);
      if (slot === 'T1') setSurfaceT1(parsed);
      else setSurfaceT2(parsed);
      markResolved(slot);
    },
    [markResolved],
  );

  useEffect(() => {
    handleRef.current = handleResponse;
  }, [handleResponse]);

  const getSurface = useCallback(
    (slot: SurfaceSlot, timestamp: string, gen: number) => {
      const channel = `${KEY}_${slot}_${gen}`;

      if (inFlightRef.current.has(channel)) return;
      inFlightRef.current.add(channel);

      const request = {
        id: channel,
        method: 'historic_vol_surface',
        params: {
          type: snapshotType,
          model: 'SVI',
          token: market,
          format: 'json',
          timestamp,
        },
        jsonrpc: '2.0',
      };

      sendMessage(JSON.stringify(request));

      subscribeToChannel(channel, (res: any) => handleRef.current(slot, gen, res));
      subscribedRef.current.add(channel);
    },
    [market, snapshotType, sendMessage, subscribeToChannel],
  );

  // Re-fetch whenever market/T1/T2 OR snapshotType change (a type change is a new
  // generation, so both slots re-fetch at the new type). Open a new generation so a
  // late response from the previous selection is ignored; reset state so a stale
  // surface never lingers; clear inFlightRef so the new requests are not blocked
  // by a prior one that never resolved. The cleanup unsubscribes this
  // generation's channels before the next one opens, and on unmount.
  useEffect(() => {
    if (!market || !t1 || !t2) return;

    const gen = (genRef.current += 1);

    setSurfaceT1(null);
    setSurfaceT2(null);
    setError(null);
    inFlightRef.current.clear();
    pendingRef.current = new Set<SurfaceSlot>(['T1', 'T2']);
    setLoading(true);

    getSurface('T1', t1, gen);
    getSurface('T2', t2, gen);

    return () => {
      subscribedRef.current.forEach((channel) => unsubscribeFromChannel(channel));
      subscribedRef.current.clear();
    };
  }, [market, t1, t2, getSurface, unsubscribeFromChannel]);

  const surfaceDelta = useMemo(
    () => computeSurfaceDelta(surfaceT1, surfaceT2),
    [surfaceT1, surfaceT2],
  );

  const value = useMemo<IMarketChangeSurfaceProvider>(
    () => ({ surfaceT1, surfaceT2, surfaceDelta, snapshotType, setSnapshotType, loading, error }),
    [surfaceT1, surfaceT2, surfaceDelta, snapshotType, setSnapshotType, loading, error],
  );

  return (
    <MarketChangeSurfaceContext.Provider value={value}>
      {children}
    </MarketChangeSurfaceContext.Provider>
  );
};
